import React, { useState, useEffect, useRef } from 'react';
import { fmtMonto } from 'shared/format';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import VisorComprobante from 'shared/components/VisorComprobante';
import {
  FiCreditCard,
  FiCalendar,
  FiDollarSign,
  FiAlertTriangle,
  FiCheckCircle,
  FiMapPin,
  FiUpload,
  FiSend,
  FiEye,
  FiClock,
  FiRefreshCw,
  FiCopy,
  FiCheck,
  FiImage,
  FiX,
} from 'react-icons/fi';
import { C } from 'shared/darkColors';

const fechaLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default function Pago() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPagar, setShowPagar] = useState(false);
  const [pagando, setPagando] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [referencia, setReferencia] = useState('');
  const [meses, setMeses] = useState(1);
  const [metodos, setMetodos] = useState([]);
  const [metodoSel, setMetodoSel] = useState(null);
  const [tasa, setTasa] = useState(null);
  const [copiado, setCopiado] = useState('');
  const [preview, setPreview] = useState(null);
  const [viewPreview, setViewPreview] = useState(false);
  const [visorUrl, setVisorUrl] = useState(null);
  const fileRef = useRef(null);
  const { plan_tipo, tieneVisibilidad, tienePermiso } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const puedeGestionar = tienePermiso('plan.gestionar');

  const [showCambio, setShowCambio] = useState(false);
  const [tipoObj, setTipoObj] = useState(null);
  const [sedesObj, setSedesObj] = useState(null);
  const [cotiza, setCotiza] = useState(null);
  const [cotizando, setCotizando] = useState(false);
  const [compCambio, setCompCambio] = useState(null);
  const [metodoCambio, setMetodoCambio] = useState('transferencia');
  const [refCambio, setRefCambio] = useState('');
  const [enviandoCambio, setEnviandoCambio] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const fileCambioRef = useRef(null);

  const load = () => {
    API.get('/suscripciones/mi-suscripcion')
      .then((res) => {
        if (res.data.status === 'success') setData(res.data.data);
      })
      .catch((e) => console.error('Error cargando suscripcion:', e?.message || e))
      .finally(() => setLoading(false));
  };

  const loadSolicitudes = () => {
    API.get('/empresa/upgrade-plan/mis-solicitudes')
      .then((res) => {
        if (res.data.status === 'success') setSolicitudes(res.data.data || []);
      })
      .catch(() => {});
  };

  const loadMetodos = () => {
    API.get('/pago-config/metodos')
      .then((res) => {
        if (res.data.status === 'success') {
          const lista = res.data.data || [];
          setMetodos(lista);
          if (lista.length > 0) {
            setMetodoSel(lista[0].codigo);
            setMetodoCambio(lista[0].codigo);
          }
        }
      })
      .catch(() => {});
  };

  const loadTasa = () => {
    API.get('/pago-config/tasa')
      .then((res) => {
        if (res.data.status === 'success') setTasa(res.data.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    loadSolicitudes();
    loadMetodos();
    loadTasa();
  }, []);

  useEffect(() => {
    if (!showCambio || !tipoObj || !sedesObj) return;
    setCotizando(true);
    const t = setTimeout(() => {
      API.get('/empresa/upgrade-plan/cotizar', {
        params: { plan_tipo: tipoObj, sedes_objetivo: sedesObj },
      })
        .then((res) => {
          if (res.data.status === 'success') setCotiza(res.data.data);
        })
        .catch(() => setCotiza(null))
        .finally(() => setCotizando(false));
    }, 250);
    return () => clearTimeout(t);
  }, [showCambio, tipoObj, sedesObj]);

  const abrirCambio = () => {
    setTipoObj(plan_tipo === 'externo' ? 'externo' : 'interno');
    setSedesObj(data?.sede_count || 1);
    setCotiza(null);
    setCompCambio(null);
    setRefCambio('');
    setShowCambio(true);
  };

  const enviarCambio = async () => {
    if (!cotiza || cotiza.bloqueado || cotiza.sin_cambio || !cotiza.plan_nuevo) return;
    if (cotiza.requiere_pago && !compCambio) return alert('Adjunta el comprobante de pago');
    setEnviandoCambio(true);
    try {
      const fd = new FormData();
      if (tipoObj) fd.append('plan_tipo', tipoObj);
      if (sedesObj) fd.append('sedes_objetivo', sedesObj);
      if (cotiza.requiere_pago) {
        fd.append('archivo', compCambio);
        fd.append('metodo', metodoCambio);
        if (refCambio) fd.append('referencia', refCambio);
      }
      const res = await API.post('/empresa/upgrade-plan', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data.data;
      if (d.aplicado) {
        try {
          const u = JSON.parse(localStorage.getItem('usuario') || '{}');
          if (d.plan_tipo) {
            u.plan_tipo = d.plan_tipo;
            localStorage.setItem('usuario', JSON.stringify(u));
          }
        } catch (e) {
          /* noop */
        }
        alert('Plan actualizado.');
        window.location.reload();
        return;
      }
      alert('Solicitud enviada. El super admin verificara el pago y aplicara el cambio.');
      setShowCambio(false);
      setCompCambio(null);
      setRefCambio('');
      load();
      loadSolicitudes();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al solicitar el cambio');
    } finally {
      setEnviandoCambio(false);
    }
  };

  const handlePagar = async () => {
    if (!comprobante) return alert('Selecciona el comprobante de pago');
    setPagando(true);
    try {
      const fd = new FormData();
      fd.append('archivo', comprobante);
      fd.append('metodo', metodoSel || 'usdt_binance');
      fd.append('meses', meses);
      if (referencia) fd.append('referencia', referencia);
      await API.post('/suscripciones/pagar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Pago enviado. El superadmin verificará tu pago.');
      setShowPagar(false);
      setComprobante(null);
      setReferencia('');
      setMeses(1);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setPagando(false);
    }
  };

  const precioMensual = data?.precio_mensual_sede_usd || 0;
  const totalPago = precioMensual * meses;
  const metodoActual = metodos.find((m) => m.codigo === metodoSel) || metodos[0] || null;
  const mostrarTasaBs = !!metodoActual?.mostrar_tasa_bs;
  const tasaReal = tasa?.tasa ?? null;
  const margenRecargo = tasa?.margen_comision ?? 0;
  const tasaFinal =
    tasa?.tasa_final ?? (tasaReal != null ? tasaReal * (1 + margenRecargo) : null);
  const totalBs = mostrarTasaBs && tasaFinal != null ? totalPago * tasaFinal : null;

  const formatearBs = (n) =>
    n != null ? `Bs ${fmtMonto(n)}` : null;

  const copiar = (texto, etiqueta) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(texto).then(() => {
        setCopiado(etiqueta || texto);
        setTimeout(() => setCopiado(''), 1500);
      });
    }
  };

  const onSelectComprobante = (e) => {
    const f = e.target.files[0];
    setComprobante(f || null);
    if (f && f.type && f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const camposPago = metodoActual && metodoActual.campos && metodoActual.campos.length
    ? metodoActual.campos
    : [];

  const renderPagoForm = () => (
    <>
      <div
        style={{
          fontSize: '16px',
          fontWeight: 700,
          color: 'var(--agx-text)',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <span
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '10px',
            background: 'var(--agx-blue-bg)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FiCreditCard size={17} color="var(--agx-pri2)" />
        </span>
        {data?.plan_nombre ? `Pagar ${data.plan_nombre}` : 'Subir comprobante de pago'}
      </div>

      {metodos.length > 0 && (
        <div className="pago-metodos" style={{ marginBottom: '20px' }}>
          <label style={C.formLabel}>Método de pago</label>
          <div className="pago-metodos-grid">
            {metodos.map((m) => {
              const activo = m.codigo === metodoSel;
              return (
                <button
                  key={m.codigo}
                  type="button"
                  onClick={() => setMetodoSel(m.codigo)}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '16px 10px 14px',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    border: activo ? '2px solid var(--agx-pri2)' : '1.5px solid var(--agx-border)',
                    background: activo ? 'var(--agx-blue-bg)' : 'var(--agx-surface)',
                    boxShadow: activo ? '0 2px 8px rgba(41,82,204,0.12)' : 'none',
                    transition: 'all 0.15s ease',
                    fontFamily: 'inherit',
                  }}
                >
                  {activo && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: 'var(--agx-pri2)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FiCheck size={12} color="#fff" />
                    </span>
                  )}
                  <span style={{ fontSize: '26px', lineHeight: 1 }}>{m.icono || '💳'}</span>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'var(--agx-text)',
                      textAlign: 'center',
                      lineHeight: 1.25,
                    }}
                  >
                    {m.nombre}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {metodoActual && (
        <div
          style={{
            marginBottom: '20px',
            padding: '16px',
            borderRadius: '14px',
            background: 'var(--agx-surface)',
            border: '1.5px solid var(--agx-border)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: 700,
              color: 'var(--agx-text)',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '18px' }}>{metodoActual.icono || '💳'}</span>
            {metodoActual.nombre || 'Método de pago'}
          </div>
          {metodoActual.descripcion && (
            <div
              style={{
                fontSize: '12.5px',
                color: 'var(--agx-muted)',
                marginBottom: '12px',
                lineHeight: 1.45,
              }}
            >
              {metodoActual.descripcion}
            </div>
          )}
          {camposPago.map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                padding: '9px 0',
                borderBottom: i === camposPago.length - 1 ? 'none' : '1px solid var(--agx-bg2)',
                fontSize: '13px',
              }}
            >
              <span style={{ color: 'var(--agx-muted)', flexShrink: 0 }}>{c.etiqueta}</span>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 700,
                  color: 'var(--agx-text)',
                  minWidth: 0,
                }}
              >
                <span style={{ overflowWrap: 'anywhere', textAlign: 'right' }}>{c.valor}</span>
                <button
                  type="button"
                  onClick={() => copiar(c.valor, c.valor)}
                  style={{
                    background: copiado === c.valor ? 'var(--agx-green-bg)' : 'var(--agx-blue-bg)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px 8px',
                    borderRadius: '7px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: copiado === c.valor ? 'var(--agx-green)' : 'var(--agx-pri2)',
                    flexShrink: 0,
                  }}
                  title="Copiar"
                >
                  {copiado === c.valor ? <FiCheck size={12} /> : <FiCopy size={12} />}
                  {copiado === c.valor ? 'Copiado' : 'Copiar'}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={C.formLabel}>Duración del pago</label>
        <div className="pago-meses">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => {
            const activo = meses === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setMeses(n)}
                style={{
                  padding: '10px 0',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: activo ? '2px solid var(--agx-pri2)' : '1.5px solid var(--agx-border)',
                  background: activo ? 'var(--agx-blue-bg)' : 'var(--agx-surface)',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: activo ? 'var(--agx-pri2)' : 'var(--agx-text)',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s ease',
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '14px',
          background: 'var(--agx-bg2)',
          border: '1px solid var(--agx-border)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: 'var(--agx-muted)',
            marginBottom: '8px',
          }}
        >
          <span>
            {meses} mes{meses !== 1 ? 'es' : ''} × ${precioMensual}/mes
          </span>
          <span style={{ fontWeight: 700, color: 'var(--agx-text)' }}>
            ${fmtMonto(totalPago)}
          </span>
        </div>
        {totalBs && tasaReal != null && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '12.5px',
                color: 'var(--agx-muted)',
                marginBottom: '4px',
              }}
            >
              <span>Tasa real: 1 USDT ≈ Bs {tasaReal.toLocaleString('es-VE')}</span>
              {margenRecargo > 0 && (
                <span style={{ fontWeight: 600, color: 'var(--agx-warning-text)' }}>
                  Recargo {Math.round(margenRecargo * 100)}%
                </span>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '12.5px',
                color: 'var(--agx-muted)',
                marginBottom: '10px',
              }}
            >
              <span>Total aproximado en bolívares</span>
              <span style={{ fontWeight: 700, color: 'var(--agx-green)' }}>{formatearBs(totalBs)}</span>
            </div>
          </>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '12px',
            borderTop: '1px dashed var(--agx-border)',
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--agx-text)' }}>
            Total a pagar
          </span>
          <span style={{ fontSize: '24px', fontWeight: 800, color: 'var(--agx-pri2)' }}>
            ${fmtMonto(totalPago)}
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={C.formLabel}>Comprobante de pago</label>
        {comprobante ? (
          <div
            style={{
              borderRadius: '14px',
              border: '1.5px solid var(--agx-green)',
              background: 'var(--agx-surface)',
              overflow: 'hidden',
            }}
          >
            {preview ? (
              <div
                onClick={() => setViewPreview(true)}
                style={{
                  position: 'relative',
                  background: 'var(--agx-bg2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'zoom-in',
                  maxHeight: '340px',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={preview}
                  alt="Vista previa del comprobante"
                  style={{
                    width: '100%',
                    maxHeight: '340px',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    right: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '7px 12px',
                    borderRadius: '10px',
                    background: 'rgba(0,0,0,0.65)',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  <FiEye size={14} /> Ampliar
                </span>
              </div>
            ) : (
              <div
                style={{
                  padding: '40px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  background: 'var(--agx-bg2)',
                }}
              >
                <span
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '16px',
                    background: 'var(--agx-blue-bg)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FiImage size={28} color="var(--agx-pri2)" />
                </span>
                <div style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
                  Archivo PDF adjunto
                </div>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 16px',
                borderTop: '1px solid var(--agx-bg2)',
              }}
            >
              <span
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '9px',
                  background: 'var(--agx-green-bg)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FiCheck size={16} color="var(--agx-green)" />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--agx-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {comprobante.name}
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--agx-muted)', marginTop: '1px' }}>
                  {comprobante.size > 1024 * 1024
                    ? `${(comprobante.size / (1024 * 1024)).toFixed(2)} MB`
                    : `${Math.max(1, Math.round(comprobante.size / 1024))} KB`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '7px 12px',
                  borderRadius: '9px',
                  border: '1.5px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-text)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                <FiUpload size={13} /> Cambiar
              </button>
              <button
                type="button"
                onClick={() => {
                  setComprobante(null);
                  setPreview(null);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '7px 12px',
                  borderRadius: '9px',
                  border: '1.5px solid var(--agx-red-bg)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-red)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                <FiX size={13} /> Quitar
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              padding: '36px 20px',
              borderRadius: '14px',
              border: '1.5px dashed var(--agx-border)',
              background: 'var(--agx-surface)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '18px',
                background: 'var(--agx-blue-bg)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FiUpload size={28} color="var(--agx-pri2)" />
            </span>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--agx-text)' }}>
              Subir comprobante de pago
            </div>
            <div style={{ fontSize: '12px', color: 'var(--agx-muted)', lineHeight: 1.4 }}>
              Haz clic para seleccionar tu comprobante
              <br />
              PNG, JPG o PDF
            </div>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          onChange={onSelectComprobante}
          style={{ display: 'none' }}
        />
      </div>

      {viewPreview && preview && (
        <div
          onClick={() => setViewPreview(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 4000,
            padding: '20px',
          }}
        >
          <img
            src={preview}
            alt="Comprobante ampliado"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '92vw',
              maxHeight: '92vh',
              borderRadius: '12px',
              objectFit: 'contain',
            }}
          />
          <button
            type="button"
            onClick={() => setViewPreview(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
            }}
          >
            <FiX size={20} />
          </button>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid var(--agx-bg2)',
        }}
      >
        <button onClick={() => setShowPagar(false)} style={C.btnCancel}>
          Cancelar
        </button>
        <button
          onClick={handlePagar}
          disabled={pagando || !comprobante || !metodoSel}
          style={{
            ...C.btnSuccess,
            padding: '12px 26px',
            fontSize: '14px',
            opacity: pagando || !comprobante || !metodoSel ? 0.6 : 1,
          }}
        >
          {pagando ? (
            'Enviando...'
          ) : (
            <>
              <FiSend size={15} /> Enviar pago ${fmtMonto(totalPago)}
            </>
          )}
        </button>
      </div>
    </>
  );

  if (loading) return <div style={C.loading}>Cargando...</div>;

  if (!data)
    return (
      <div style={C.wrapper}>
        <div style={C.title}>Plan y Pagos</div>
        <div style={C.noPlan}>Error al cargar los datos del plan.</div>
      </div>
    );

  const subEstado = data.estado || 'pendiente';
  const planCancelado = subEstado === 'cancelado';
  const planInactivo = subEstado === 'sin_plan';
  const ultimoPago = data.pagos && data.pagos.length > 0 ? data.pagos[0] : null;
  const pagoEstado = ultimoPago?.estado || subEstado;
  const pagoPendiente = pagoEstado === 'pendiente';
  const pagoVencido =
    data.dias_restantes !== null && data.dias_restantes !== undefined && data.dias_restantes < 0;
  const pagoVerificado = !pagoVencido && (pagoEstado === 'verificado' || subEstado === 'activo');
  const pagoRechazado = pagoEstado === 'rechazado';
  const cercaVencer =
    data.dias_restantes !== null &&
    data.dias_restantes !== undefined &&
    data.dias_restantes <= 7 &&
    data.dias_restantes >= 0;
  const mostrarPagar =
    !planCancelado &&
    (planInactivo ||
      subEstado === 'pendiente' ||
      pagoPendiente ||
      pagoRechazado ||
      cercaVencer ||
      pagoVencido);
  const alDia =
    !planCancelado &&
    !planInactivo &&
    subEstado === 'activo' &&
    !pagoPendiente &&
    !pagoRechazado &&
    !pagoVencido;

  const estadoInfo = pagoVerificado
    ? { label: 'Al día', bg: 'var(--agx-green-bg)', color: 'var(--agx-green)', icon: FiCheckCircle }
    : pagoPendiente
      ? { label: 'Pendiente verificación', bg: 'var(--agx-amber-bg)', color: 'var(--agx-amber)', icon: FiClock }
      : planCancelado
        ? { label: 'Cancelado', bg: 'var(--agx-bg2)', color: 'var(--agx-muted)', icon: FiAlertTriangle }
        : planInactivo
          ? { label: 'Sin plan', bg: 'var(--agx-amber-bg)', color: 'var(--agx-amber)', icon: FiAlertTriangle }
          : pagoVencido
            ? { label: 'Vencido', bg: 'var(--agx-red-bg)', color: 'var(--agx-red)', icon: FiAlertTriangle }
            : { label: 'Rechazado', bg: 'var(--agx-red-bg)', color: 'var(--agx-red)', icon: FiAlertTriangle };

  const sedesUsadas = data.sede_count || 0;
  const sedesMax = Number.isFinite(data.plan_max_sedes) ? data.plan_max_sedes : null;
  const sedesPct = sedesMax && sedesMax > 0 ? Math.min(100, Math.round((sedesUsadas / sedesMax) * 100)) : 0;
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="pago-wrapper" style={C.wrapper}>
      <style>{`
        .pago-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 24px; }
        .pago-hero-row { display: flex; justify-content: space-between; align-items: center; gap: 24px; }
        .pago-metodos-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; }
        .pago-meses { display: grid; grid-template-columns: repeat(auto-fill, minmax(56px, 1fr)); gap: 8px; }
        @media (max-width: 768px) {
          .pago-grid { grid-template-columns: 1fr !important; }
          .pago-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .pago-table-wrap table { min-width: 650px; }
          .pago-wrapper { padding: 16px 12px !important; }
          .pago-card { padding: 16px !important; }
          .pago-form-grid { grid-template-columns: 1fr !important; }
          .pago-hero { padding: 18px !important; }
          .pago-hero-row { flex-direction: column !important; align-items: flex-start !important; gap: 16px !important; }
          .pago-hero-price { text-align: left !important; }
          .pago-metodos-grid { grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); gap: 8px; }
        }
        @media (max-width: 640px) {
          .pago-required-row { flex-direction: column; align-items: flex-start; gap: 12px; }
          .pago-required-row > button { width: 100%; justify-content: center; }
          .pago-title { font-size: 20px !important; }
        }
      `}</style>
      {visorUrl && <VisorComprobante comprobanteUrl={visorUrl} onClose={() => setVisorUrl(null)} />}
      <div className="pago-header">
        <div>
          <div className="pago-title" style={C.title}>Plan y Pagos</div>
          <div style={C.subtitle}>Información de tu suscripción</div>
        </div>
      </div>

      {/* HERO — resumen visual del plan */}
      <div
        className="pago-hero"
        style={{
          background: 'var(--agx-surface)',
          border: '1px solid var(--agx-border)',
          borderLeft: '4px solid ' + estadoInfo.color,
          borderRadius: '18px',
          padding: '24px 26px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          marginBottom: '24px',
        }}
      >
        <div className="pago-hero-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '26px', fontWeight: 800, color: 'var(--agx-text)', lineHeight: 1.1 }}>
                {data.plan_nombre || 'Sin plan'}
              </span>
              <span style={C.tag('var(--agx-blue-bg)', 'var(--agx-pri2)')}>
                {plan_tipo === 'externo' ? 'Plan Externo' : 'Plan Interno'}
              </span>
              <span style={C.badge(estadoInfo.bg, estadoInfo.color)}>
                <EstadoIcon size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                {estadoInfo.label}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '18px',
                flexWrap: 'wrap',
                marginTop: '14px',
              }}
            >
              {v('vis.plan.fechas') && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: 'var(--agx-muted)',
                  }}
                >
                  <FiCalendar size={14} color="var(--agx-muted)" />
                  {data.fecha_fin
                    ? `Vence el ${fechaLocal(data.fecha_fin).toLocaleDateString()}`
                    : 'Vencimiento pendiente'}
                </span>
              )}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: 'var(--agx-muted)',
                }}
              >
                <FiMapPin size={14} color="var(--agx-muted)" />
                {sedesUsadas} sede{sedesUsadas !== 1 ? 's' : ''} activa{sedesUsadas !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {v('vis.plan.montos') && (
            <div className="pago-hero-price" style={{ textAlign: 'right', flexShrink: 0 }}>
              <div
                style={{
                  fontSize: '30px',
                  fontWeight: 800,
                  color: 'var(--agx-pri2)',
                  lineHeight: 1,
                }}
              >
                ${data.precio_mensual_sede_usd || '0'}
                <span style={{ fontSize: '13px', color: 'var(--agx-muted)', marginLeft: '4px' }}>
                  /mes
                </span>
              </div>
            </div>
          )}
        </div>

        {sedesMax && sedesMax > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: 'var(--agx-muted)', fontWeight: 600 }}>Sedes usadas</span>
              <span style={{ color: 'var(--agx-text)', fontWeight: 700 }}>
                {sedesUsadas} / {sedesMax}
              </span>
            </div>
            <div
              style={{
                height: '8px',
                borderRadius: '6px',
                background: 'var(--agx-bg2)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${sedesPct}%`,
                  borderRadius: '6px',
                  background: sedesPct >= 100 ? 'var(--agx-amber)' : 'var(--agx-pri2)',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {planInactivo && (
        <div
          style={{
            ...C.card,
            background: 'var(--agx-amber-bg)',
            border: '1px solid var(--agx-amber)',
          }}
        >
          <div
            className="pago-required-row"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--agx-amber-bg)',
                  flexShrink: 0,
                }}
              >
                <FiAlertTriangle size={20} color="var(--agx-amber)" />
              </div>
              <div>
                <div
                  style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-warning-text)' }}
                >
                  Realiza tu primer pago
                </div>
                <div
                  style={{ fontSize: '13px', color: 'var(--agx-warning-text)', marginTop: '2px' }}
                >
                  Realiza tu primer pago para empezar a utilizar la página y la app.
                </div>
              </div>
            </div>
            {!showPagar && puedeGestionar && (
              <button
                onClick={() => setShowPagar(true)}
                style={{ ...C.btnSuccess, whiteSpace: 'nowrap' }}
              >
                <FiDollarSign size={16} /> Realizar primer pago
              </button>
            )}
          </div>
          {showPagar && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--agx-amber)',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--agx-warning-text)',
                  marginBottom: '12px',
                }}
              >
                Pago inicial — {data.plan_nombre || 'Plan'} (${data.precio_mensual_sede_usd || '0'}
                /mes)
              </div>
              {renderPagoForm()}
            </div>
          )}
        </div>
      )}

      {cercaVencer && (
        <div
          style={{
            ...C.card,
            background: 'var(--agx-amber-bg)',
            border: '1px solid var(--agx-amber)',
          }}
        >
          <div
            className="pago-required-row"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--agx-amber-bg)',
                  flexShrink: 0,
                }}
              >
                <FiAlertTriangle size={20} color="var(--agx-amber)" />
              </div>
              <div>
                <div
                  style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-warning-text)' }}
                >
                  Membresía por vencer
                </div>
                <div
                  style={{ fontSize: '13px', color: 'var(--agx-warning-text)', marginTop: '2px' }}
                >
                  {data.dias_restantes === 0 ? (
                    'Tu plan vence hoy. Carga tu pago para renovar y evitar la interrupción del servicio.'
                  ) : (
                    <>
                      Tu plan vence en{' '}
                      <strong>
                        {data.dias_restantes} día{data.dias_restantes !== 1 ? 's' : ''}
                      </strong>
                      . Carga tu pago para renovar y evitar la interrupción del servicio.
                    </>
                  )}
                </div>
              </div>
            </div>
            {!showPagar && puedeGestionar && (
              <button
                onClick={() => setShowPagar(true)}
                style={{ ...C.btnPrimary, background: 'var(--agx-amber)', whiteSpace: 'nowrap' }}
              >
                <FiDollarSign size={16} /> Renovar ahora
              </button>
            )}
          </div>
          {showPagar && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--agx-amber)',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--agx-warning-text)',
                  marginBottom: '12px',
                }}
              >
                Pago para renovación — próximo mes
              </div>
              {renderPagoForm()}
            </div>
          )}
        </div>
      )}

      {pagoVencido && !cercaVencer && (
        <div
          style={{ ...C.card, background: 'var(--agx-red-bg)', border: '1px solid var(--agx-red)' }}
        >
          <div
            className="pago-required-row"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--agx-red-bg)',
                  flexShrink: 0,
                }}
              >
                <FiAlertTriangle size={20} color="#b71c1c" />
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-red)' }}>
                  Membresía vencida
                </div>
                <div
                  style={{ fontSize: '13px', color: 'var(--agx-danger-text)', marginTop: '2px' }}
                >
                  Tu plan venció hace{' '}
                  <strong>
                    {Math.abs(data.dias_restantes)} día
                    {Math.abs(data.dias_restantes) !== 1 ? 's' : ''}
                  </strong>
                  . Carga tu pago para reactivar el servicio.
                </div>
              </div>
            </div>
            {!showPagar && puedeGestionar && (
              <button
                onClick={() => setShowPagar(true)}
                style={{ ...C.btnPrimary, background: 'var(--agx-red)', whiteSpace: 'nowrap' }}
              >
                <FiDollarSign size={16} /> Reactivar membresía
              </button>
            )}
          </div>
          {showPagar && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--agx-red)',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--agx-red)',
                  marginBottom: '12px',
                }}
              >
                Pago para reactivación
              </div>
              {renderPagoForm()}
            </div>
          )}
        </div>
      )}

      {mostrarPagar && !cercaVencer && !pagoVencido && (
        <div style={C.card}>
          <div
            className="pago-required-row"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-text)' }}>
                {pagoRechazado ? 'Pago rechazado - Reintenta' : 'Pago requerido'}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--agx-muted)', marginTop: '4px' }}>
                {pagoRechazado
                  ? 'Tu pago anterior fue rechazado. Sube un nuevo comprobante.'
                  : 'Sube el comprobante de pago para activar tu plan.'}
              </div>
              {pagoRechazado && ultimoPago?.motivo_rechazo && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: 'var(--agx-red-bg)',
                    border: '1px solid var(--agx-red)',
                    fontSize: '12.5px',
                    color: 'var(--agx-danger-text)',
                    lineHeight: 1.4,
                  }}
                >
                  <strong>Motivo del rechazo:</strong> {ultimoPago.motivo_rechazo}
                </div>
              )}
            </div>
            {!showPagar && puedeGestionar && (
              <button onClick={() => setShowPagar(true)} style={C.btnSuccess}>
                <FiDollarSign size={16} /> Pagar ${data.precio_mensual_sede_usd}
              </button>
            )}
          </div>

          {showPagar && (
            <div
              style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid var(--agx-border)',
              }}
            >
              {renderPagoForm()}
            </div>
          )}
        </div>
      )}

      {planCancelado && (
        <div style={{ ...C.card, background: 'var(--agx-bg2)', border: '1px solid #ddd' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--agx-skeleton)',
                flexShrink: 0,
              }}
            >
              <FiAlertTriangle size={20} color="var(--agx-muted)" />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-text)' }}>
                Plan cancelado
              </div>
              <div style={{ fontSize: '13px', color: 'var(--agx-muted)', marginTop: '2px' }}>
                Tu suscripción fue cancelada. Contacta al administrador del sistema para reactivar
                tu plan.
              </div>
            </div>
          </div>
        </div>
      )}

      {alDia && (
        <div style={C.card}>
          <div
            className="pago-required-row"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-text)' }}>
                Extender licencia
              </div>
              <div style={{ fontSize: '13px', color: 'var(--agx-muted)', marginTop: '4px' }}>
                Tu plan vence el{' '}
                {data.fecha_fin ? fechaLocal(data.fecha_fin).toLocaleDateString() : '—'}. Puedes
                pagar por adelantado uno o varios meses y se sumarán al vencimiento actual.
              </div>
            </div>
            {!showPagar && puedeGestionar && (
              <button onClick={() => setShowPagar(true)} style={{ ...C.btnPrimary, whiteSpace: 'nowrap' }}>
                <FiCalendar size={15} /> Pagar por adelantado
              </button>
            )}
          </div>

          {showPagar && (
            <div
              style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid var(--agx-border)',
              }}
            >
              {renderPagoForm()}
            </div>
          )}
        </div>
      )}

      {!planCancelado && (
        <div style={C.card}>
          <div
            className="pago-required-row"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-text)' }}>
                Cambiar de plan
              </div>
              <div style={{ fontSize: '13px', color: 'var(--agx-muted)', marginTop: '4px' }}>
                Cambia entre Interno y Externo o ajusta tus sedes. Los upgrades se cobran
                prorrateados por los dias restantes; los downgrades se aplican al instante sin
                costo.
              </div>
            </div>
            {!showCambio && puedeGestionar && (
              <button onClick={abrirCambio} style={{ ...C.btnPrimary, whiteSpace: 'nowrap' }}>
                <FiRefreshCw size={15} /> Cambiar de plan
              </button>
            )}
          </div>

          {showCambio && (
            <div
              style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid var(--agx-border)',
              }}
            >
              <div style={C.formGroup}>
                <label style={C.formLabel}>Tipo de plan</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[
                    { v: 'interno', l: 'Interno', d: 'Inventario, POS y catalogo completos' },
                    { v: 'externo', l: 'Externo', d: 'Solo ventas, pedidos y delivery' },
                  ].map((o) => (
                    <div
                      key={o.v}
                      onClick={() => setTipoObj(o.v)}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        border: tipoObj === o.v ? '2px solid var(--agx-pri2)' : '1px solid #ddd',
                        background: tipoObj === o.v ? 'var(--agx-blue-bg)' : '#fff',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: tipoObj === o.v ? 'var(--agx-text)' : 'var(--agx-text)',
                        }}
                      >
                        {o.l}
                      </div>
                      <div
                        style={{ fontSize: '11px', color: 'var(--agx-muted)', marginTop: '2px' }}
                      >
                        {o.d}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={C.formGroup}>
                <label style={C.formLabel}>Sedes ({data.sede_count || 1} activas)</label>
                <input
                  type="number"
                  min={data.sede_count || 1}
                  value={sedesObj || ''}
                  onChange={(e) =>
                    setSedesObj(Math.max(data.sede_count || 1, parseInt(e.target.value, 10) || 1))
                  }
                  style={{ ...C.formInput, maxWidth: '160px' }}
                />
                <div style={{ fontSize: '11px', color: 'var(--agx-muted)', marginTop: '4px' }}>
                  No puedes bajar de tus sedes activas. Para reducir, desactiva sedes primero.
                </div>
              </div>

              <div
                style={{
                  borderRadius: '10px',
                  background: 'var(--agx-bg2)',
                  border: '1px solid var(--agx-border)',
                  padding: '14px 16px',
                  marginBottom: '14px',
                }}
              >
                {cotizando ? (
                  <div style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>Calculando...</div>
                ) : !cotiza ? (
                  <div style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
                    Selecciona una opción para ver el costo.
                  </div>
                ) : cotiza.bloqueado ? (
                  <div style={{ fontSize: '13px', color: 'var(--agx-red)', fontWeight: 600 }}>
                    {cotiza.bloqueado.razon}
                  </div>
                ) : cotiza.sin_cambio ? (
                  <div style={{ fontSize: '13px', color: 'var(--agx-amber)', fontWeight: 600 }}>
                    Ya tienes este plan y número de sedes.
                  </div>
                ) : cotiza.plan_nuevo ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '13px',
                        marginBottom: '6px',
                      }}
                    >
                      <span style={{ color: 'var(--agx-muted)' }}>Plan destino</span>
                      <span
                        style={{ fontWeight: 600, color: 'var(--agx-text)', textAlign: 'right' }}
                      >
                        {cotiza.plan_nuevo.nombre} · {cotiza.plan_nuevo.tipo} · hasta{' '}
                        {cotiza.plan_nuevo.max_sedes ?? '∞'} sedes
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '13px',
                        marginBottom: '6px',
                      }}
                    >
                      <span style={{ color: 'var(--agx-muted)' }}>Mensual</span>
                      <span style={{ fontWeight: 600, color: 'var(--agx-text)' }}>
                        ${cotiza.plan_nuevo.mensual_usd}/mes
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '14px',
                        paddingTop: '8px',
                        borderTop: '1px dashed #ddd',
                      }}
                    >
                      <span style={{ color: 'var(--agx-text)', fontWeight: 600 }}>
                        {cotiza.requiere_pago
                          ? `A pagar ahora (prorrateado, ${cotiza.dias_restantes} dias)`
                          : 'A pagar ahora'}
                      </span>
                      <span
                        style={{
                          fontWeight: 800,
                          color: cotiza.requiere_pago ? 'var(--agx-green)' : 'var(--agx-pri2)',
                        }}
                      >
                        ${cotiza.monto_usd}
                      </span>
                    </div>
                    {!cotiza.requiere_pago && (
                      <div
                        style={{ fontSize: '11px', color: 'var(--agx-green)', marginTop: '6px' }}
                      >
                        Sin costo: se aplica de inmediato.
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {cotiza && cotiza.requiere_pago && !cotiza.bloqueado && !cotiza.sin_cambio && (
                <>
                  <div
                    style={{
                      marginBottom: '14px',
                      padding: '14px',
                      borderRadius: '10px',
                      background: 'var(--agx-blue-bg)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                    }}
                    onClick={() => fileCambioRef.current?.click()}
                  >
                    <FiUpload size={20} color="var(--agx-pri2)" />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {compCambio ? compCambio.name : 'Seleccionar comprobante'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>
                        PNG, JPG o PDF
                      </div>
                    </div>
                    <input
                      ref={fileCambioRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setCompCambio(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div
                    className="pago-form-grid"
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}
                  >
                    <div>
                      <label style={C.formLabel}>Metodo de pago</label>
                      <select
                        value={metodoCambio}
                        onChange={(e) => setMetodoCambio(e.target.value)}
                        style={C.formSelect}
                      >
                        {metodos.map((m) => (
                          <option key={m.codigo} value={m.codigo}>
                            {m.icono || '💳'} {m.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={C.formLabel}>Referencia (opcional)</label>
                      <input
                        value={refCambio}
                        onChange={(e) => setRefCambio(e.target.value)}
                        style={C.formInput}
                        placeholder="Num. operacion"
                      />
                    </div>
                  </div>
                  {(() => {
                    const mc = metodos.find((m) => m.codigo === metodoCambio);
                    if (!mc || !(mc.campos || []).length) return null;
                    return (
                      <div
                        style={{
                          marginTop: '12px',
                          padding: '12px 14px',
                          borderRadius: '10px',
                          background: 'var(--agx-blue-bg)',
                          border: '1px solid var(--agx-border)',
                        }}
                      >
                        {mc.descripcion && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: 'var(--agx-muted)',
                              marginBottom: '8px',
                            }}
                          >
                            {mc.descripcion}
                          </div>
                        )}
                        {(mc.campos || []).map((c, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 0',
                              fontSize: '12.5px',
                            }}
                          >
                            <span style={{ color: 'var(--agx-muted)' }}>{c.etiqueta}</span>
                            <span style={{ fontWeight: 700, color: 'var(--agx-text)' }}>
                              {c.valor}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end',
                  marginTop: '16px',
                }}
              >
                <button
                  onClick={() => setShowCambio(false)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    background: 'var(--agx-surface)',
                    color: 'var(--agx-text)',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={enviarCambio}
                  disabled={
                    enviandoCambio ||
                    cotizando ||
                    !cotiza ||
                    !!cotiza.bloqueado ||
                    cotiza.sin_cambio ||
                    !cotiza.plan_nuevo ||
                    (cotiza.requiere_pago && !compCambio)
                  }
                  style={{
                    ...(cotiza && cotiza.requiere_pago ? C.btnSuccess : C.btnPrimary),
                    opacity:
                      enviandoCambio ||
                      cotizando ||
                      !cotiza ||
                      !!cotiza.bloqueado ||
                      cotiza.sin_cambio ||
                      !cotiza.plan_nuevo ||
                      (cotiza.requiere_pago && !compCambio)
                        ? 0.6
                        : 1,
                  }}
                >
                  {cotiza && cotiza.requiere_pago ? (
                    <FiSend size={14} />
                  ) : (
                    <FiCheckCircle size={14} />
                  )}
                  {enviandoCambio
                    ? 'Procesando...'
                    : cotiza && cotiza.requiere_pago
                      ? 'Enviar solicitud y pago'
                      : 'Aplicar cambio'}
                </button>
              </div>
            </div>
          )}

          {solicitudes.length > 0 && (
            <div
              style={{
                marginTop: '18px',
                paddingTop: '16px',
                borderTop: '1px solid var(--agx-border)',
              }}
            >
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'var(--agx-text)',
                  marginBottom: '10px',
                }}
              >
                Solicitudes de cambio
              </div>
              {solicitudes.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--agx-bg2)',
                    fontSize: '12.5px',
                  }}
                >
                  <span style={{ color: 'var(--agx-text)' }}>
                    {s.plan_actual_nombre || '—'} → <strong>{s.plan_nuevo_nombre}</strong> ·{' '}
                    {s.sedes_objetivo} sede(s){s.monto_usd > 0 ? ` · $${s.monto_usd}` : ' · gratis'}
                  </span>
                  <span
                    style={C.badge(
                      s.estado === 'aplicado'
                        ? 'var(--agx-green-bg)'
                        : s.estado === 'pendiente'
                          ? 'var(--agx-amber-bg)'
                          : 'var(--agx-red-bg)',
                      s.estado === 'aplicado'
                        ? 'var(--agx-green)'
                        : s.estado === 'pendiente'
                          ? 'var(--agx-amber)'
                          : 'var(--agx-red)'
                    )}
                  >
                    {s.estado}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {data.pagos && data.pagos.length > 0 && (
        <div style={C.card}>
          <div
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--agx-text)',
              marginBottom: '16px',
            }}
          >
            Ultimos pagos
          </div>
          <div className="pago-table-wrap">
            <table style={C.table}>
              <thead>
                <tr>
                  <th style={C.th}>Fecha</th>
                  <th style={C.th}>Monto</th>
                  <th style={C.th}>Metodo</th>
                  <th style={C.th}>Referencia</th>
                  <th style={C.th}>Comprobante</th>
                  <th style={C.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.pagos.map((p, i) => (
                  <tr key={i}>
                    <td style={C.td}>
                      {p.fecha_pago ? fechaLocal(p.fecha_pago).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ ...C.td, fontWeight: 600 }}>${p.monto}</td>
                    <td style={C.td}>{p.metodo || '—'}</td>
                    <td style={C.td}>{p.referencia || '—'}</td>
                    <td style={C.td}>
                      {p.comprobante_url ? (
                        <button onClick={() => setVisorUrl(p.comprobante_url)} style={C.btnVer}>
                          <FiEye size={12} style={{ marginRight: '3px' }} />
                          Ver
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={C.td}>
                      <span
                        style={C.badge(
                          p.estado === 'verificado'
                            ? 'var(--agx-green-bg)'
                            : p.estado === 'pendiente'
                              ? 'var(--agx-amber-bg)'
                              : 'var(--agx-red-bg)',
                          p.estado === 'verificado'
                            ? 'var(--agx-green)'
                            : p.estado === 'pendiente'
                              ? 'var(--agx-amber)'
                              : 'var(--agx-red)'
                        )}
                      >
                        {p.estado || 'pendiente'}
                      </span>
                      {p.estado === 'rechazado' && p.motivo_rechazo && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: 'var(--agx-red)',
                            marginTop: '4px',
                            maxWidth: '220px',
                          }}
                          title={p.motivo_rechazo}
                        >
                          {p.motivo_rechazo}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
